import { effect, inject, Signal, TemplateRef, untracked } from '@angular/core';
import { translate } from '@jsverse/transloco';
import { ToastMgr } from '@shared/components/toast/toast-mgr';
import { ToastRef, ToastTemplateContent } from '@shared/components/toast/toast.model';
import { pageReloader } from '@shared/utility/session-local-storage';
import { NewVersionChecker } from './new-version-checker';
import { VersionUpdateStatus } from './new-version-checker.model';
import { VersionUpdateToastContext } from './version-update-toast.model';

/**
 * Mostra lo stato dell'aggiornamento di versione in un toast `sticky`, aggiornato sul posto a ogni cambio di stato.
 * Chiuso dall'utente, il toast si riapre solo al cambio di stato successivo.
 */
export const injectVersionUpdateToast = (template: Signal<TemplateRef<VersionUpdateToastContext>>): void => {
  const toastMgr = inject(ToastMgr);
  const newVersionChecker = inject(NewVersionChecker);
  let toast: ToastRef<VersionUpdateToastContext> | null = null;

  effect(() => {
    const status = newVersionChecker.updateStatus();
    if (!status) {
      return;
    }
    untracked(() => {
      const content = toastContent(template(), status);
      if (toast?.isOpen()) {
        toast.update({ content });
        return;
      }
      toast = toastMgr.show({ severity: 'info', sticky: true, closable: true, content });
    });
  });
};

const toastContent = (template: TemplateRef<VersionUpdateToastContext>, status: VersionUpdateStatus) => {
  const context: VersionUpdateToastContext = {
    status,
    reload: () => {
      pageReloader.reload();
    }
  };
  const content: ToastTemplateContent<VersionUpdateToastContext> = {
    kind: 'template',
    template,
    context,
    announcement: announcementOf(status)
  };
  return content;
};

const announcementOf = (status: VersionUpdateStatus): string => {
  if (status === 'downloading') {
    return translate('versionInfo.downloading');
  }
  return `${translate('versionInfo.newVersionAvailable')} ${translate('versionInfo.reloadPageToUpdate')}`;
};
